import AlertIcon from '@assets/icons/alert.svg';
import Analytics from '@/utils/analytics';
import ChevronFilters from '@assets/icons/chevron-filters.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AvailableOrderFulfillmentFieldsFragment,
  NewOrder_OrderFieldsFragment,
} from '@/types';
import {
  calendarDateToMoment,
  formatTimeslot,
  momentToCalendarDate,
} from '@/utils/date';
import {
  cartHasLeadTimeItem,
  OrderAvailability,
} from '@domain/fulfillment-settings/blocs/order-availability';
import { createStyles } from '@/contexts/theme-context';
import {
  DatePickerCalendar,
  DatePickerDayTones,
  DatePickerExclude,
} from '@usepepper/sprout-kit-ui';
import { FallbackConfirmationActionSheet } from '@/components/fallback-confirmation-action-sheet';
import { FONTS, SCREEN_PADDING, shadow, TYPOGRAPHY } from '@/styles';
import { FulfillmentActionSheetTimeslotView } from './fulfillment-action-sheet-timeslot-view';
import { FulfillmentActionSheetViewContainer } from './fulfillment-action-sheet-view-container';
import { Moment } from 'moment';
import { SelectInput } from '@/components/select-input';
import { Text, useWindowDimensions, View } from 'react-native';
import {
  Timeslot,
  TimeslotOption,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { TrackingProperties } from '@/utils/analytics/base-reporter';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { Type } from '@/components/toast';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemLeadTimeContext } from '@/contexts/item-lead-time-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';

const TIMESLOT_LIST_MAX_HEIGHT = 280;

interface BaseProps {
  clearTimeslot: () => void;
  draftOrder: NewOrder_OrderFieldsFragment | null;
  fulfillmentOption: AvailableOrderFulfillmentFieldsFragment;
  initialFulfillmentDate: Moment;
  isSaving: boolean;
  onDayChange: (date: Moment) => void;
  onSave: (
    date: Moment,
    nextTimeslot: Timeslot | null,
    resolvedFulfillmentOption: AvailableOrderFulfillmentFieldsFragment,
  ) => void;
  selectedTimeslot: Timeslot | null;
  trackingProperties: TrackingProperties | null;
}

interface RoutedTimeslotsProps extends BaseProps {
  jumpToTimeslots: (timeslotOptions: TimeslotOption[]) => void;
  onSelectTimeslot?: never;
}

interface FloatingTimeslotsProps extends BaseProps {
  jumpToTimeslots?: never;
  onSelectTimeslot: (timeslot: Timeslot) => void;
}

type Props = RoutedTimeslotsProps | FloatingTimeslotsProps;

export const FulfillmentActionSheetFulfillmentDaysView: React.FC<Props> = ({
  clearTimeslot,
  draftOrder,
  fulfillmentOption,
  initialFulfillmentDate,
  isSaving,
  jumpToTimeslots,
  onDayChange,
  onSave,
  onSelectTimeslot,
  selectedTimeslot,
  trackingProperties,
}) => {
  const [styles, theme] = useStyles();
  const { availableOrderFulfillments, supplierTimezone } = useNewOrderContext();
  const { variantPackHasLeadTimePolicy } = useItemLeadTimeContext();
  const { closeToast, showToast } = useToasts();
  const { supplierEmployeeRole, selectedRestaurantRelationship } =
    useAuthenticatedUserContext();
  const { isSmallLayout } = useLayoutContext();

  const additionalOrderValidationRolesConfig = useSupplierDynamicConfigValue(
    'additional_order_validation_supplier_roles',
  );

  const timezoneMap = useMemo<TimeZoneMap>(() => {
    return {
      restaurant: selectedRestaurantRelationship.timezone,
      supplier: supplierTimezone,
    };
  }, [selectedRestaurantRelationship.timezone, supplierTimezone]);

  const hasLeadTimeItem = useMemo(
    () =>
      cartHasLeadTimeItem(
        draftOrder?.orders_items ?? [],
        variantPackHasLeadTimePolicy,
      ),
    [draftOrder, variantPackHasLeadTimePolicy],
  );

  const orderAvailability = useMemo(
    () =>
      new OrderAvailability(
        hasLeadTimeItem
          ? [fulfillmentOption]
          : [fulfillmentOption, ...availableOrderFulfillments],
        timezoneMap,
        fulfillmentOption.fulfillment_name,
        true,
      ),
    [
      availableOrderFulfillments,
      fulfillmentOption,
      hasLeadTimeItem,
      timezoneMap,
    ],
  );

  const [selectedFulfillmentDate, setSelectedFulfillmentDate] =
    useState<Moment | null>(() =>
      orderAvailability.isValidSupplierFulfillmentDate(
        initialFulfillmentDate,
        supplierEmployeeRole,
        additionalOrderValidationRolesConfig ?? [],
      )
        ? initialFulfillmentDate
        : orderAvailability.nextFulfillmentDayMoment,
    );
  const [pendingFallbackSave, setPendingFallbackSave] = useState<{
    date: Moment;
    fulfillmentOption: AvailableOrderFulfillmentFieldsFragment;
    timeslot: Timeslot | null;
  } | null>(null);

  useEffect(() => {
    if (selectedFulfillmentDate) {
      onDayChange(selectedFulfillmentDate);
    }
  }, [selectedFulfillmentDate, onDayChange]);

  const [invalidDateToastUUID, setInvalidDateToastUUID] = useState<string>();
  const [isTimeslotListOpen, setIsTimeslotListOpen] = useState(false);
  const [isTimeslotListAbove, setIsTimeslotListAbove] = useState(false);
  const timeslotFieldRef = useRef<View>(null);
  const { height: windowHeight } = useWindowDimensions();

  const fulfillmentDateRange = useMemo(
    () =>
      orderAvailability.getFulfillmentDateRange(
        supplierEmployeeRole,
        additionalOrderValidationRolesConfig ?? [],
      ),
    [
      additionalOrderValidationRolesConfig,
      orderAvailability,
      supplierEmployeeRole,
    ],
  );

  const calendarExclude = useMemo<DatePickerExclude | undefined>(
    () =>
      fulfillmentDateRange.disabledDates.length > 0
        ? { date: fulfillmentDateRange.disabledDates.map(momentToCalendarDate) }
        : undefined,
    [fulfillmentDateRange],
  );

  const calendarDayTones = useMemo<DatePickerDayTones>(
    () => ({
      warning: fulfillmentDateRange.fallbackDates.map(momentToCalendarDate),
    }),
    [fulfillmentDateRange],
  );

  const selectedDates = useMemo(
    () =>
      selectedFulfillmentDate
        ? [momentToCalendarDate(selectedFulfillmentDate)]
        : [],
    [selectedFulfillmentDate],
  );

  const timeslotOptionsForDay = useMemo<TimeslotOption[]>(
    () =>
      selectedFulfillmentDate
        ? orderAvailability.getTimeslotOptionsForDate(
            selectedFulfillmentDate,
            supplierEmployeeRole,
            additionalOrderValidationRolesConfig ?? [],
          )
        : [],
    [
      additionalOrderValidationRolesConfig,
      orderAvailability,
      selectedFulfillmentDate,
      supplierEmployeeRole,
    ],
  );

  useEffect(() => {
    if (!selectedTimeslot) {
      return;
    }
    if (
      !timeslotOptionsForDay.some(
        (ts) =>
          ts.metadata?.timeslot.startTime === selectedTimeslot.startTime &&
          ts.metadata?.timeslot.endTime === selectedTimeslot.endTime &&
          ts.metadata?.disabled === false,
      )
    ) {
      clearTimeslot();
    }
  }, [timeslotOptionsForDay, selectedTimeslot, clearTimeslot]);

  return (
    <FulfillmentActionSheetViewContainer
      isSaveDisabled={!selectedFulfillmentDate}
      isSaving={isSaving}
      onSavePress={() => {
        if (!selectedFulfillmentDate) {
          return;
        }

        const resolvedFulfillmentOption =
          orderAvailability.resolveAvailabilityForDate(
            selectedFulfillmentDate,
            supplierEmployeeRole,
            additionalOrderValidationRolesConfig ?? [],
          );

        if (!resolvedFulfillmentOption) {
          return;
        }

        const nextTimeslot =
          timeslotOptionsForDay.length > 0 ? selectedTimeslot : null;

        if (
          resolvedFulfillmentOption === orderAvailability.selectedAvailability
        ) {
          onSave(
            selectedFulfillmentDate,
            nextTimeslot,
            resolvedFulfillmentOption,
          );
          return;
        }

        setPendingFallbackSave({
          date: selectedFulfillmentDate,
          fulfillmentOption: resolvedFulfillmentOption,
          timeslot: nextTimeslot,
        });
      }}
      saveButtonTrackingData={{
        name: 'order_select_date.save_button',
        properties: {
          date: selectedFulfillmentDate?.toISOString() ?? null,
          orderUUID: draftOrder?.uuid ?? null,
          type: fulfillmentOption.fulfillment_name,
          ...trackingProperties,
        },
      }}
    >
      <View
        style={isSmallLayout ? styles.calendarContainerSmall : null}
        testID="fulfillment-calendar"
      >
        <DatePickerCalendar
          dayLabels={{
            warning: i18n.t('fulfillment_date_select.day_label_warning'),
          }}
          dayTones={calendarDayTones}
          exclude={calendarExclude}
          maxDate={
            fulfillmentDateRange.latestDate
              ? momentToCalendarDate(fulfillmentDateRange.latestDate)
              : undefined
          }
          minDate={momentToCalendarDate(fulfillmentDateRange.earliestDate)}
          mode="single"
          onChange={([date]) => {
            if (!date) {
              return;
            }

            Analytics.track('order_select_date.date.change', {
              orderUUID: draftOrder?.uuid ?? null,
            });

            if (invalidDateToastUUID) {
              closeToast(invalidDateToastUUID);
            }

            setSelectedFulfillmentDate(
              calendarDateToMoment(date, timezoneMap.restaurant),
            );
          }}
          onDisabledPress={() => {
            Analytics.track('order_select_date.invalid_date.press', {
              orderUUID: draftOrder?.uuid ?? null,
            });

            if (invalidDateToastUUID) {
              closeToast(invalidDateToastUUID);
            }

            const toastUUID = showToast({
              content: (
                <View style={styles.invalidDateToast}>
                  <View style={styles.invalidDateToastIcon}>
                    <AlertIcon
                      fill={theme.colors.white}
                      height={20}
                      width={20}
                    />
                  </View>
                  <View style={styles.invalidDateToastContent}>
                    <Text style={TYPOGRAPHY.toast}>
                      {translateFulfillmentType(
                        fulfillmentOption.fulfillment_name,
                        'toast.invalid_date_header',
                      )}
                    </Text>
                    <Text
                      style={[TYPOGRAPHY.toast, styles.invalidDateToastMessage]}
                    >
                      {translateFulfillmentType(
                        fulfillmentOption.fulfillment_name,
                        'toast.invalid_date_message',
                      )}
                    </Text>
                  </View>
                </View>
              ),
              type: Type.NEGATIVE,
            });
            setInvalidDateToastUUID(String(toastUUID));
          }}
          value={selectedDates}
          width="100%"
        />
      </View>
      {timeslotOptionsForDay.length > 0 ? (
        <View
          ref={timeslotFieldRef}
          style={onSelectTimeslot ? styles.timeslotFieldAnchor : null}
        >
          <SelectInput
            inputContainerStyle={[
              styles.timeslotButton,
              isSmallLayout ? styles.timeslotButtonSmall : null,
            ]}
            label={selectedTimeslot ? '' : i18n.t('select_time_slot')}
            onPress={() => {
              if (!onSelectTimeslot) {
                jumpToTimeslots?.(timeslotOptionsForDay);
                return;
              }
              if (isTimeslotListOpen) {
                setIsTimeslotListOpen(false);
                return;
              }
              timeslotFieldRef.current?.measureInWindow(
                (_x, y, _width, height) => {
                  setIsTimeslotListAbove(
                    windowHeight - y - height <
                      TIMESLOT_LIST_MAX_HEIGHT + theme.sk.spacing.spaceSmall,
                  );
                  setIsTimeslotListOpen(true);
                },
              );
            }}
            rightElement={
              <ChevronFilters
                fill={theme.colors.primary}
                height={8}
                width={14}
              />
            }
            value={
              selectedTimeslot
                ? formatTimeslot(selectedTimeslot, timezoneMap, true)
                : ''
            }
          />
          {onSelectTimeslot && isTimeslotListOpen ? (
            <FulfillmentActionSheetTimeslotView
              onSelect={(timeslot) => {
                onSelectTimeslot(timeslot);
                setIsTimeslotListOpen(false);
              }}
              style={[
                styles.floatingTimeslotList,
                isTimeslotListAbove
                  ? styles.floatingTimeslotListAbove
                  : styles.floatingTimeslotListBelow,
              ]}
              timeslotOptions={timeslotOptionsForDay}
            />
          ) : null}
        </View>
      ) : null}
      {pendingFallbackSave ? (
        <FallbackConfirmationActionSheet
          fulfillmentDate={pendingFallbackSave.date}
          isVisible
          onClose={() => setPendingFallbackSave(null)}
          onContinue={() => {
            Analytics.track(
              'order_select_date.fallback_confirmation.continue',
              {
                orderUUID: draftOrder?.uuid ?? null,
                type: pendingFallbackSave.fulfillmentOption.fulfillment_name,
              },
            );
            onSave(
              pendingFallbackSave.date,
              pendingFallbackSave.timeslot,
              pendingFallbackSave.fulfillmentOption,
            );
            setPendingFallbackSave(null);
          }}
        />
      ) : null}
    </FulfillmentActionSheetViewContainer>
  );
};

const FLOATING_LIST_SHADOW = shadow({
  elevation: 6,
  opacity: 0.05,
  radius: 12,
  x: 0,
  y: 0,
});

const useStyles = createStyles((theme) => ({
  calendarContainerSmall: {
    paddingHorizontal: SCREEN_PADDING,
  },
  floatingTimeslotList: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    left: 0,
    maxHeight: TIMESLOT_LIST_MAX_HEIGHT,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    ...FLOATING_LIST_SHADOW,
  },
  floatingTimeslotListAbove: {
    bottom: '100%',
    marginBottom: theme.sk.spacing.spaceSmall,
  },
  floatingTimeslotListBelow: {
    marginTop: theme.sk.spacing.spaceSmall,
    top: '100%',
  },
  invalidDateToast: {
    flexDirection: 'row',
  },
  invalidDateToastContent: {
    flex: 1,
  },
  invalidDateToastIcon: {
    marginRight: theme.sk.spacing.spaceSMedium,
  },
  invalidDateToastMessage: {
    ...FONTS.primary,
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  timeslotButton: {
    borderColor: theme.colors.stroke,
    borderWidth: 1,
    marginTop: theme.sk.spacing.spaceMedium,
  },
  timeslotButtonSmall: {
    marginHorizontal: SCREEN_PADDING,
  },
  timeslotFieldAnchor: {
    zIndex: 1,
  },
}));
